import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowRight,
  ArrowLeft,
  ShieldCheck,
  RotateCcw,
  Sparkles,
  CheckCircle2,
  AlertTriangle,
  Stethoscope,
  Activity,
} from "lucide-react";
import { api } from "@/lib/api";
import {
  QUIZ_SYSTEMS,
  QUIZ_CONDITIONS,
  RELEVANCE_THRESHOLD,
  scoreQuiz,
} from "@/lib/quiz-conditions";

/**
 * FIND MY RECOVERY FORMULA — condition-first, safety-aware engine.
 *
 * Flow:
 *   1. System selector (Joint / Spine / Vascular / etc.)
 *   2. Condition selector for that system
 *   3. Condition-specific questions (single + multi-select, with red-flag safety options)
 *   4. Safety gate: any red-flag → urgent-assessment result (no products)
 *   5. Weighted score → primary + optional supporting recommendations
 *   6. No forced recommendation when max score < RELEVANCE_THRESHOLD
 */

function Progress({ step, total }) {
  return (
    <div className="mb-8 flex items-center gap-1.5" data-testid="quiz-progress">
      {Array.from({ length: total }).map((_, i) => (
        <div
          key={i}
          className={`h-1 flex-1 rounded-full ${i < step ? "bg-[#34D8A0]" : "bg-white/15"}`}
        />
      ))}
    </div>
  );
}

function SystemSelector({ onPick }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="rounded-2xl border border-white/10 bg-white/[0.03] p-6 backdrop-blur-sm sm:p-8"
      data-testid="system-selector"
    >
      <div className="mb-2 font-mono text-[11px] uppercase tracking-overline text-white/40">Step 1 · Choose a system</div>
      <h2 className="font-display text-2xl font-bold leading-snug tracking-tight sm:text-3xl">
        What are you looking for support with?
      </h2>
      <div className="mt-6 grid gap-3 sm:grid-cols-2">
        {QUIZ_SYSTEMS.map((s) => (
          <button
            key={s.key}
            onClick={() => onPick(s)}
            className="group flex items-center justify-between rounded-xl border border-white/10 bg-white/[0.03] px-4 py-4 text-left transition-all hover:border-[#34D8A0] hover:bg-white/[0.06] sm:px-5"
            data-testid={`sys-${s.key}`}
          >
            <div className="min-w-0">
              <div className="font-display text-base font-bold tracking-tight sm:text-lg" style={{ color: s.tint }}>{s.name}</div>
              <div className="mt-1 font-mono text-[10px] uppercase tracking-overline text-white/40">
                {s.conditions.length} {s.conditions.length === 1 ? "pathway" : "pathways"}
              </div>
            </div>
            <ArrowRight className="h-4 w-4 shrink-0 text-white/40 transition-all group-hover:translate-x-1 group-hover:text-[#34D8A0]" />
          </button>
        ))}
      </div>
    </motion.div>
  );
}

function ConditionSelector({ system, onPick, onBack }) {
  const conds = system.conditions.map((slug) => QUIZ_CONDITIONS[slug]).filter(Boolean);
  return (
    <motion.div
      key={system.key}
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      transition={{ duration: 0.35 }}
      className="rounded-2xl border border-white/10 bg-white/[0.03] p-6 backdrop-blur-sm sm:p-8"
      data-testid="condition-selector"
    >
      <div className="mb-2 font-mono text-[11px] uppercase tracking-overline" style={{ color: system.tint }}>
        {system.name} · Step 2
      </div>
      <h2 className="font-display text-2xl font-bold leading-snug tracking-tight sm:text-3xl">
        Which pathway fits your situation?
      </h2>
      <div className="mt-6 grid gap-3">
        {conds.map((c) => (
          <button
            key={c.slug}
            onClick={() => onPick(c)}
            className="group flex items-center justify-between rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3.5 text-left transition-all hover:border-[#34D8A0] hover:bg-white/[0.06] sm:px-5"
            data-testid={`cond-${c.slug}`}
          >
            <div className="min-w-0">
              <div className="font-display text-base font-bold tracking-tight text-white">{c.name}</div>
              <div className="mt-0.5 truncate text-xs leading-relaxed text-white/55">{c.tagline}</div>
            </div>
            <ArrowRight className="h-4 w-4 shrink-0 text-white/40 transition-all group-hover:translate-x-1 group-hover:text-[#34D8A0]" />
          </button>
        ))}
      </div>
      <button
        onClick={onBack}
        className="mt-6 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-overline text-white/40 hover:text-white"
        data-testid="back-to-systems"
      >
        <ArrowLeft className="h-3.5 w-3.5" /> Back to systems
      </button>
    </motion.div>
  );
}

function QuestionCard({ condition, qIndex, question, answers, onAnswer, onBack, onMultiNext, onRestart }) {
  const isMulti = question.type === "multi";
  const total = condition.questions.length;
  const currentMulti = answers[question.id];

  const toggle = (v) => {
    const arr = Array.isArray(currentMulti) ? currentMulti : [];
    const next = arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v];
    onAnswer(question.id, next);
  };

  return (
    <motion.div
      key={`${condition.slug}-${question.id}`}
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      transition={{ duration: 0.3 }}
      className="rounded-2xl border border-white/10 bg-white/[0.03] p-6 backdrop-blur-sm sm:p-8"
      data-testid={`quiz-step-${question.id}`}
    >
      <div className="mb-2 flex items-center justify-between font-mono text-[11px] uppercase tracking-overline text-white/40">
        <span>{condition.tag} · Step {qIndex + 1} of {total}</span>
        <button onClick={onRestart} className="text-white/40 hover:text-white" data-testid="quiz-restart-inline">
          <RotateCcw className="h-3.5 w-3.5" />
        </button>
      </div>
      <h2 className="font-display text-xl font-bold leading-snug tracking-tight sm:text-2xl">
        {question.q}
      </h2>
      <div className="mt-6 grid gap-2.5">
        {question.opts.map((o) => {
          const isRed = !!o.redFlag;
          const selected = isMulti ? (Array.isArray(currentMulti) && currentMulti.includes(o.v)) : false;
          const cls = isRed
            ? "border-[#ef4444]/40 bg-[#ef4444]/[0.06] hover:border-[#ef4444] text-[#fecaca]"
            : "border-white/10 bg-white/[0.03] hover:border-[#34D8A0] hover:bg-white/[0.06] text-white";
          const sel = selected ? "border-[#34D8A0] bg-[#34D8A0]/[0.09]" : "";
          return (
            <button
              key={o.v}
              onClick={() => (isMulti ? toggle(o.v) : onAnswer(question.id, o.v))}
              className={`group flex items-center justify-between rounded-xl border px-4 py-3.5 text-left text-sm font-medium transition-all sm:px-5 sm:text-base ${cls} ${sel}`}
              data-testid={`opt-${question.id}-${o.v}`}
            >
              <span className="flex items-center gap-2">
                {isMulti && (
                  <span className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${selected ? "border-[#34D8A0] bg-[#34D8A0] text-black" : "border-white/25"}`}>
                    {selected && <CheckCircle2 className="h-3 w-3" />}
                  </span>
                )}
                {isRed && <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-[#ef4444]" />}
                <span>{o.l}</span>
              </span>
              {!isMulti && <ArrowRight className="h-4 w-4 shrink-0 text-white/30 transition-all group-hover:translate-x-1 group-hover:text-[#34D8A0]" />}
            </button>
          );
        })}
      </div>
      <div className="mt-6 flex items-center justify-between">
        <button
          onClick={onBack}
          className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-overline text-white/40 hover:text-white"
          data-testid="quiz-back"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Back
        </button>
        {isMulti && (
          <button
            onClick={onMultiNext}
            disabled={!Array.isArray(currentMulti) || currentMulti.length === 0}
            className="inline-flex items-center gap-2 rounded-full bg-[#34D8A0] px-5 py-2.5 font-mono text-[11px] font-bold uppercase tracking-overline text-black transition-transform hover:scale-[1.03] disabled:cursor-not-allowed disabled:bg-white/20 disabled:text-white/40"
            data-testid="quiz-next"
          >
            Next <ArrowRight className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    </motion.div>
  );
}

function UrgentResult({ condition, onRestart }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="space-y-6"
      data-testid="quiz-result-urgent"
    >
      <div className="rounded-2xl border border-[#ef4444]/40 bg-[#ef4444]/[0.08] p-6 sm:p-8">
        <div className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-overline text-[#fca5a5]">
          <AlertTriangle className="h-3.5 w-3.5" /> {condition.result_headline || "Your Next Step"}
        </div>
        <h2 className="mt-3 font-display text-2xl font-black leading-tight tracking-tight text-white sm:text-3xl">
          Please seek professional assessment.
        </h2>
        <p className="mt-3 text-sm leading-relaxed text-white/80 sm:text-base">
          Based on your answers, some of the symptoms you selected may need appropriate medical or specialist assessment. Physio Essential does not recommend selecting a nutritional supplement based on this questionnaire in this situation.
        </p>
        {condition.disclaimer && (
          <p className="mt-4 rounded-md border border-white/10 bg-white/[0.03] p-3 text-xs leading-relaxed text-white/60">
            {condition.disclaimer}
          </p>
        )}
      </div>

      <div className="flex flex-wrap gap-3">
        <Link
          to="/contact"
          className="inline-flex items-center gap-2 rounded-full bg-[#34D8A0] px-6 py-3 font-semibold text-black transition-transform hover:scale-[1.03]"
          data-testid="urgent-find-professional"
        >
          Find a Qualified Professional <ArrowRight className="h-4 w-4" />
        </Link>
        <Link
          to={condition.protocol_link || "/for-physiotherapists"}
          className="inline-flex items-center gap-2 rounded-full border border-white/25 px-6 py-3 font-semibold text-white hover:bg-white hover:text-black"
          data-testid="urgent-learn-more"
        >
          Learn About Recovery Support
        </Link>
        <button
          onClick={onRestart}
          className="inline-flex items-center gap-2 rounded-full border border-white/15 px-5 py-2.5 font-mono text-[11px] uppercase tracking-overline text-white/70 hover:border-white hover:text-white"
          data-testid="quiz-restart"
        >
          <RotateCcw className="h-3.5 w-3.5" /> Start over
        </button>
      </div>
    </motion.div>
  );
}

function StandardResult({ condition, scores, products, onRestart }) {
  const findProduct = (slug) => products.find((p) => p.slug === slug);
  const eligible = scores.filter((s) => s.score >= RELEVANCE_THRESHOLD);
  const primary = eligible.find((s) => s.role === "primary") || eligible[0] || null;
  const supporting = eligible.filter((s) => primary && s.slug !== primary.slug).slice(0, 2);

  // No forced recommendation
  if (!primary) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="space-y-6"
        data-testid="quiz-result-no-match"
      >
        <div className="rounded-2xl border border-white/15 bg-white/[0.04] p-6 sm:p-8">
          <div className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-overline text-white/50">
            <Stethoscope className="h-3.5 w-3.5" /> {condition.result_headline}
          </div>
          <h2 className="mt-3 font-display text-2xl font-black leading-tight tracking-tight text-white sm:text-3xl">
            Consider discussing this with a healthcare professional.
          </h2>
          <p className="mt-3 text-sm leading-relaxed text-white/70 sm:text-base">
            We don't have enough information to confidently match you with a specific formula. Your answers suggest a personalised assessment would be more helpful than a supplement recommendation right now.
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          <Link to={condition.protocol_link} className="inline-flex items-center gap-2 rounded-full bg-[#34D8A0] px-6 py-3 font-semibold text-black hover:scale-[1.03]" data-testid="no-match-protocol">
            {condition.protocol_label} <ArrowRight className="h-4 w-4" />
          </Link>
          <button onClick={onRestart} className="inline-flex items-center gap-2 rounded-full border border-white/15 px-5 py-2.5 font-mono text-[11px] uppercase tracking-overline text-white/70 hover:border-white hover:text-white" data-testid="quiz-restart">
            <RotateCcw className="h-3.5 w-3.5" /> Start over
          </button>
        </div>
      </motion.div>
    );
  }

  const primaryProduct = findProduct(primary.slug);
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="space-y-6"
      data-testid="quiz-result"
    >
      <div className="rounded-2xl border border-[#34D8A0]/40 bg-[#34D8A0]/[0.06] p-6 sm:p-8">
        <div className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-overline text-[#34D8A0]">
          <Sparkles className="h-3.5 w-3.5" /> {condition.result_headline}
        </div>
        <div className="mt-3 font-mono text-xs uppercase tracking-overline text-white/50">Primary focus</div>
        <div className="mt-1 font-display text-2xl font-bold tracking-tight text-white sm:text-3xl">
          {condition.result_focus}
        </div>
        <div className="mt-1 text-sm text-white/60">Category: {condition.result_category}</div>

        <div className="mt-6 flex flex-col gap-6 lg:flex-row lg:items-start">
          {primaryProduct?.image && (
            <img
              src={primaryProduct.image}
              alt={primaryProduct.name}
              className="mx-auto h-32 w-32 object-contain drop-shadow-[0_20px_30px_rgba(0,0,0,0.5)] sm:h-40 sm:w-40"
            />
          )}
          <div className="min-w-0 flex-1">
            <div className="font-mono text-xs uppercase tracking-overline text-white/50">Recommended Formula</div>
            <div className="mt-1 font-display text-2xl font-black tracking-tight text-white sm:text-3xl">
              {primaryProduct?.name || primary.slug}
            </div>
            <p className="mt-2 text-sm leading-relaxed text-white/70">
              {primaryProduct?.short_description || primaryProduct?.description?.slice(0, 160) || ""}
            </p>
            <div className="mt-3 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/[0.04] px-3 py-1 font-mono text-[10px] uppercase tracking-overline text-white/60">
              Relevance {primary.score}
            </div>
          </div>
        </div>
      </div>

      {/* Supporting */}
      {supporting.length > 0 && (
        <div className="grid gap-3 sm:grid-cols-2">
          {supporting.map((s) => {
            const p = findProduct(s.slug);
            return (
              <div key={s.slug} className="flex items-center justify-between rounded-2xl border border-white/10 bg-white/[0.03] p-4" data-testid={`supporting-${s.slug}`}>
                <div className="min-w-0">
                  <div className="font-mono text-[10px] uppercase tracking-overline text-white/50">Optional Supporting</div>
                  <div className="mt-1 truncate font-display text-base font-bold tracking-tight text-white">{p?.name || s.slug}</div>
                  <div className="mt-0.5 font-mono text-[10px] text-white/40">Relevance {s.score}</div>
                </div>
                <Link
                  to={`/product/${s.slug}`}
                  className="ml-3 shrink-0 rounded-full border border-white/25 px-4 py-1.5 font-mono text-[10px] uppercase tracking-overline text-white hover:bg-white hover:text-black"
                  data-testid={`view-supporting-${s.slug}`}
                >
                  View
                </Link>
              </div>
            );
          })}
        </div>
      )}

      {/* Next steps */}
      <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
        <div className="font-mono text-[11px] uppercase tracking-overline text-[#2F80ED]">Suggested Next Step</div>
        <p className="mt-2 text-sm leading-relaxed text-white/70">
          Continue your prescribed rehabilitation programme and discuss nutritional supplementation with your healthcare professional where appropriate.
        </p>
      </div>

      {/* CTAs */}
      <div className="flex flex-wrap gap-3">
        <Link
          to={`/product/${primary.slug}`}
          className="inline-flex items-center gap-2 rounded-full bg-[#34D8A0] px-6 py-3 font-semibold text-black transition-transform hover:scale-[1.03]"
          data-testid="view-recommended"
        >
          View Recommended Formula <ArrowRight className="h-4 w-4" />
        </Link>
        <Link
          to={condition.protocol_link}
          className="inline-flex items-center gap-2 rounded-full border border-white/25 px-6 py-3 font-semibold text-white hover:bg-white hover:text-black"
          data-testid="protocol-cta"
        >
          {condition.protocol_label}
        </Link>
        <button
          onClick={onRestart}
          className="inline-flex items-center gap-2 rounded-full border border-white/15 px-5 py-2.5 font-mono text-[11px] uppercase tracking-overline text-white/70 hover:border-white hover:text-white"
          data-testid="quiz-restart"
        >
          <RotateCcw className="h-3.5 w-3.5" /> Start over
        </button>
      </div>

      {/* Disclaimer */}
      <div className="mt-2 flex items-start gap-3 rounded-md border border-white/10 bg-white/[0.02] p-4 text-xs leading-relaxed text-white/50">
        <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-white/40" />
        <span>
          {condition.disclaimer ||
            "This tool provides general nutritional information and does not diagnose medical conditions or replace professional medical advice. Physio Essential products are nutritional supplements intended to complement — not replace — physiotherapy, rehabilitation or prescribed medication."}
        </span>
      </div>
    </motion.div>
  );
}

export default function FindMyFormula() {
  const [system, setSystem] = useState(null);      // selected system object
  const [condition, setCondition] = useState(null); // selected condition config
  const [qIndex, setQIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [products, setProducts] = useState([]);
  const [showResult, setShowResult] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get("/products?limit=100").then(({ data }) => setProducts(data.items || data || [])).catch(() => {});
  }, []);

  const totalQ = condition ? condition.questions.length : 0;
  const currentQ = condition && qIndex < totalQ ? condition.questions[qIndex] : null;

  const restart = () => {
    setSystem(null);
    setCondition(null);
    setAnswers({});
    setQIndex(0);
    setShowResult(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleAnswer = (id, value) => {
    const nextAnswers = { ...answers, [id]: value };
    setAnswers(nextAnswers);
    // Detect red flag on the just-answered question — if triggered, jump straight to urgent result.
    const q = condition.questions.find((qq) => qq.id === id);
    if (q) {
      const vals = Array.isArray(value) ? value : [value];
      const triggered = vals.some((v) => {
        const opt = q.opts.find((o) => o.v === v);
        return opt && opt.redFlag;
      });
      if (triggered) {
        setShowResult(true);
        window.scrollTo({ top: 0, behavior: "smooth" });
        return;
      }
    }
    // For single-select, advance immediately
    if (q && q.type !== "multi") {
      advance();
    }
  };

  const advance = () => {
    if (qIndex + 1 >= totalQ) {
      setShowResult(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      setQIndex((s) => s + 1);
    }
  };

  const back = () => {
    if (qIndex === 0) {
      // back to condition selector
      setCondition(null);
      setAnswers({});
    } else {
      setQIndex((s) => Math.max(0, s - 1));
    }
  };

  const { scores, redFlag } = useMemo(() => {
    if (!condition || !showResult) return { scores: [], redFlag: false };
    return scoreQuiz(condition.slug, answers);
  }, [condition, answers, showResult]);

  const forceUrgent = condition && (condition.no_product_pathway || redFlag);

  return (
    <div className="grain relative min-h-screen overflow-hidden bg-[#0A2540] pt-[68px] text-white lg:pt-[116px]" data-testid="find-formula-page">
      {/* Ambient */}
      <div className="pointer-events-none absolute -left-40 top-40 h-96 w-96 rounded-full bg-[#2F80ED]/15 blur-[120px]" />
      <div className="pointer-events-none absolute -right-40 bottom-0 h-96 w-96 rounded-full bg-[#34D8A0]/15 blur-[120px]" />

      <section className="relative z-10 mx-auto max-w-3xl px-5 py-10 sm:px-8 sm:py-16">
        <div className="mb-8">
          <p className="font-mono text-xs uppercase tracking-overline text-[#34D8A0]">Recovery Guide</p>
          <h1 className="mt-3 font-display text-4xl font-black leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">
            Find My<br />Recovery Formula.
          </h1>
          <p className="mt-3 max-w-lg text-sm leading-relaxed text-white/60 sm:text-base">
            A guided assessment tailored to your recovery pathway. Answer a few condition-specific questions and we'll match nutritional support that fits.
          </p>
        </div>

        {condition && !showResult && (
          <Progress step={qIndex + 1} total={totalQ} />
        )}

        <AnimatePresence mode="wait">
          {!system && (
            <SystemSelector key="sys" onPick={setSystem} />
          )}
          {system && !condition && (
            <ConditionSelector key="cond" system={system} onPick={(c) => { setCondition(c); setQIndex(0); setAnswers({}); }} onBack={() => setSystem(null)} />
          )}
          {condition && !showResult && currentQ && (
            <QuestionCard
              key={`${condition.slug}-${qIndex}`}
              condition={condition}
              qIndex={qIndex}
              question={currentQ}
              answers={answers}
              onAnswer={handleAnswer}
              onBack={back}
              onMultiNext={advance}
              onRestart={restart}
            />
          )}
          {showResult && condition && (
            forceUrgent
              ? <UrgentResult key="urgent" condition={condition} onRestart={restart} />
              : <StandardResult key="std" condition={condition} scores={scores} products={products} onRestart={restart} />
          )}
        </AnimatePresence>
      </section>
    </div>
  );
}
